<template>
  <div class="patient">
    <router-link :to="gotoUrl">
      <PatientSelect class="select" v-model="select[patient.id]">
      </PatientSelect>
      <!-- Lower Section -->
      <!-- Envelop Icon / Phone Icon -->
      <StatusIcon
        v-if="getUserType !== 'observer'"
        class="status"
        :effect="'dark'"
        :open-delay="2000"
        :placement="'bottom'"
        :has-no-email-or-phone="checkIfHasNoEmailOrPhone(patient)"
        :has-mobile-app="checkIfHasApp(patient)"
        :has-verified-email="checkIfEmailVerif(patient)"
        :has-verified-phone="checkIfPhoneVerif(patient)"
        :tooltip-text="calculateTooltip(patient)"
      >
      </StatusIcon>
      <!-- Survey Count Icon -->
      <el-tooltip class="item" :open-delay="2000" effect="dark" content="Pending Surveys" placement="bottom">
        <div v-if="getUnansweredSurveyCount" :class="['unanswered-survey-count', patient.problem_count ? 'shift-left' : '']">
          {{ getUnansweredSurveyCount }}
        </div>
      </el-tooltip>
      <!-- Red Flag Icon -->
      <FlagIcon
        :class="[getUserType === 'observer' ? 'safe-problem-icon' : 'problem-icon']"
        v-if="patient.problem_count">
      </FlagIcon>

      <!-- Upper section -->
      <span v-if="patient.hasOwnProperty('last_ctc_push')" class="emr-icon">
        <div class="tooltip">
          <img src="../card/image/emr.png">
          <span class="tooltiptext" v-html="getEmrContent(patient.last_ctc_push)"></span>
        </div>
      </span>

      <el-tooltip class="item" :open-delay="1000" effect="dark" content="Assessment / Plan Available" placement="bottom">
        <div v-if="patient.ai" :class="['ai-icon', patient.hasOwnProperty('last_ctc_push') ? 'shift-ai-left' : '']">
          <img src="../card/image/ai.png">
        </div>
      </el-tooltip>

      <!-- Patient name, condition -->
      <h2 v-if="sort === 'last'">
        <strong>{{ patient.family_name ? patient.family_name : patient.name.trim().split(' ').pop() }}</strong
        >,&nbsp;<span class="light">{{ patient.given_name ? patient.given_name : patient.name.trim().split(' ')[0] }}</span>
      </h2>
      <template v-else-if="getUserType === 'observer'">
        <h2>
          {{ patient.name }}
          <DeviceTagIcon
            v-for="tag in patient.tag"
            :key="tag.id"
            :color="getTagColor(tag.id)"
          ></DeviceTagIcon>
        </h2>
      </template>
      <h2 v-else-if="sort === 'name'">
        <strong>{{ patient.given_name ? patient.given_name : patient.name.trim().split(' ')[0] }}</strong
        >&nbsp;<span class="light">{{ patient.family_name ? patient.family_name : patient.name.trim().split(' ').pop() }}</span>
      </h2>
      <h2 v-else>{{ getPatientName ? getPatientName : patient.name }}</h2>

      <!-- Green Yellow Red Dot Icon -->
      <p>
        <span v-if="getUserType === 'observer' && patient.next_schedule">
          {{ formatDate(patient.next_schedule) }} &bull;
          {{ patient.next_schedule_event }}
        </span>
        <span class="active" v-else-if="patient.previous_schedule && $props.url.includes('backward')">
          <QuestionProgressIcon
            v-if="patient.progress"
            :progress="patient.progress"
          ></QuestionProgressIcon>
          {{ formatDate(patient.previous_schedule) }}<span v-if="patient.previous_schedule % 1000 !== 123">, {{ formatTime(patient.previous_schedule) }}</span>
        </span>
        <span class="active" v-else-if="patient.next_schedule">
          <QuestionProgressIcon
            v-if="patient.progress"
            :progress="patient.progress"
          ></QuestionProgressIcon>
          {{ formatDate(patient.next_schedule) }}<span v-if="patient.next_schedule % 1000 !== 123">, {{ formatTime(patient.next_schedule) }}</span>
        </span>
        <span class="active" v-else>
          <template v-if="inLastNDays(7, lastTime)">
            Active this week
          </template>
          <template v-else-if="inLastNDays(30, lastTime)">
            Active this month
          </template>
          <template v-else-if="inLastNDays(90, lastTime)">
            Active 3 months ago
          </template>
          <template v-else>
            Active 6 months ago
          </template>
        </span>
      </p>
      <!-- <p v-else>&nbsp;</p> -->
    </router-link>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import FlagIcon from "./icon/Flag";
import StatusIcon from "./icon/StatusIcon";
import QuestionProgressIcon from "./icon/QuestionProgress";
import PatientSelect from "./PatientSelect";
import DeviceTagIcon from "@/module/component/DeviceTagIcon";

import formatDate from "@/module/util/format-date";
import formatTime from '@/module/util/format-time'

export default {
  name: "population-list-patient",

  props: {
    url: {
      type: String,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    sort: {
      type: String,
    },
    select: {
      type: Object,
    },
    filter: {
      type: String,
    },
  },

  data() {
    return {
      selected: false,
    }
  },

  watch: {
    selected() {
      Vue.set(this.select, this.patient.id, this.selected)
    },
  },

  computed: {
    ...mapGetters("content", ["getContent", "getContentKey"]),
    ...mapGetters("provider", [
      "getProviderProfileList",
      "getTagToColorMapping",
      "getCurrentProvider",
      "getPatient"
    ]),
    ...mapGetters(["getUserType"]),
    getPatientO() {
      return this.getPatient(this.patient.id)
    },
    getPatientName() {
      return this.getPatientO ? this.getPatientO.name : null
    },
    gotoUrl() {
      let url = ''
      if(this.getCurrentProvider)
        url = this.url + "/provider/" +  this.getCurrentProvider + '/' + this.patient.id
      else if(this.patient.providerId)
        url = this.url + "/provider/" + this.patient.providerId[0] + '/' + this.patient.id

      if (this.url.includes("/reachout")) url = url + "/message"
      return url + (this.$route.fullPath.includes('?') ? ( '?' + this.$route.fullPath.split('?')[1]) : '')
    },
    lastTime() {
      return this.patient.activity || this.patient.created
    },
    getUnansweredSurveyCount() {
      return this.patient.survey ? this.patient.survey.filter(p => !p.completed).length : 0
    }
  },

  methods: {
    formatDate,
    formatTime,
    getEmrContent(obj) {
      return 'Last EMR ' + (obj.type === 'manual' ? 'Push' : 'Sync') + '<br><b>' + this.formatDate(obj.timestamp) + ', ' + this.formatTime(obj.timestamp) + '</b>'
    },
    getTagColor(tag) {
      let tagObj = this.getTagToColorMapping.filter((obj) => obj.id === tag)
      if (tagObj.length) return tagObj[0].color
      else return "#000000"
    },
    inLastNDays(n, timestamp) {
      let checkDate = new Date(timestamp)
      let today = new Date(),
        lasNday = new Date()
      lasNday.setDate(lasNday.getDate() - n);
      return checkDate < today && checkDate > lasNday
    },
    verifiedText(user) {
      return (
        (user.light ? "" : "App - ") +
        (user.verified || user.phone_verified ? "Verified" : "Unverified")
      );
    },

    isVerified(user) {
      // If the user has verified their email address or their phone number
      if (user.email) return user.verified || user.phone_verified
      // If the user has only verified their phone number
      else if (user.phone) return user.phone_verified
      // Otherwise the user is unverified
      else return false
    },

    getApp(user) {
      if (user.ios_version) return "iOS"
      if (user.android_version) return "Android"
    },

    getVerifiedIcon(verified) {
      if (verified) return "el-icon-open"
      return "el-icon-turn-off"
    },

    thisYear(timeInput) {
      return new Date(timeInput).getFullYear() === new Date().getFullYear()
    },

    checkIfHasNoEmailOrPhone(user) {
      if ((!user.hasOwnProperty("email") || (user.hasOwnProperty("email") && user.email == '')) && (!user.hasOwnProperty("phone") || (user.hasOwnProperty("phone") && user.phone == ''))) return true
      else return false
    },

    checkIfHasApp(user) {
      if(user.hasOwnProperty("android_version") || user.hasOwnProperty("ios_version")) return true
      else return false
    },
    checkIfEmailVerif(user) {
      if(user.hasOwnProperty("verified") && user.verified) return true
      else return false
    },
    checkIfPhoneVerif(user) {
      if(user.hasOwnProperty("phone_verified") && user.phone_verified) return true
      else return false
    },
    calculateTooltip(user) {
      let toolText = ''
      if(this.checkIfHasApp(user)) {
        if(this.checkIfPhoneVerif(user) && this.checkIfEmailVerif(user)) return "Has mobile app and verified email + phone"
        else if(this.checkIfPhoneVerif(user) && !this.checkIfEmailVerif(user)) return "Has mobile app and verified phone"
        else if(this.checkIfEmailVerif(user) && !this.checkIfPhoneVerif(user)) return "Has mobile app and verified email"
        else if((user.hasOwnProperty("phone") && user.phone !== '') && !this.checkIfPhoneVerif(user) && (!user.hasOwnProperty("email") || (user.hasOwnProperty("email") && user.email == ''))) return "Has mobile app and unverified phone"
        else if((user.hasOwnProperty("email") && user.email !== '') && !this.checkIfEmailVerif(user) && (!user.hasOwnProperty("phone") || (user.hasOwnProperty("phone") && user.phone == ''))) return "Has mobile app and unverified email"
        else return "Has mobile app and unverified email & phone"
      }
      else {
        if (this.checkIfHasNoEmailOrPhone(user)) return "No contact information"
        else if(this.checkIfPhoneVerif(user) && this.checkIfEmailVerif(user)) return "Verified email + phone"
        else if(this.checkIfPhoneVerif(user) && !this.checkIfEmailVerif(user)) return "Verified phone"
        else if(this.checkIfEmailVerif(user) && !this.checkIfPhoneVerif(user)) return "Verified email"
        else if((user.hasOwnProperty("phone") && user.phone !== '') && !this.checkIfPhoneVerif(user) && (!user.hasOwnProperty("email") || (user.hasOwnProperty("email") && user.email == ''))) return "Unverified phone"
        else if((user.hasOwnProperty("email") && user.email !== '') && !this.checkIfEmailVerif(user) && (!user.hasOwnProperty("phone") || (user.hasOwnProperty("phone") && user.phone == ''))) return "Unverified email"
        else return "Unverified email & phone"
      }
    }
  },

  components: {
    PatientSelect,
    QuestionProgressIcon,
    FlagIcon,
    DeviceTagIcon,
    StatusIcon
  },
};
</script>
<style lang="scss" scoped>
@import "style.scss";
</style>
